import moment from 'moment';

import { CUSTOM } from 'core/table/cellTypes';
import { DATE_FORMAT_FULL_WITH_TIME, displayDate } from 'utils/date';

import { Disable24, NavBoss24, Search, Table } from 'ui/icons';
import { AbortedBadge, InfoBadge, RecurringBadge, RunningBadge } from 'ui/badge';
import { ListType } from 'modules/domains/list';
import { createColumn } from './utils/createColumn';
import { DeleteListCell } from './components/DeleteListCell';
import { ContactCountCell } from './components/ContactCountCell';
import { NameCell } from './components/NameCell';
import { ListFilter, List } from './types';

export const PAGE_SIZES = [5, 10, 20, 50];

export const DEFAULT_LIST_TYPE_FILTER = 'All';

export const LIST_TYPE_FILTERS: ListFilter[] = [
  DEFAULT_LIST_TYPE_FILTER,
  ListType.Standard,
  ListType.Dynamic,
  ListType.Suppression,
  ListType.Internal,
];

export const STATE_SELECTORS = ['All', 'Standard', 'Dynamic', 'Suppression', 'Internal'];

const STATE_ICONS = {
  Standard: Table,
  Dynamic: Search,
  Suppression: Disable24,
  Internal: NavBoss24,
};

const STATE_BADGE = {
  Standard: InfoBadge,
  Dynamic: RecurringBadge,
  Suppression: AbortedBadge,
  Internal: RunningBadge,
};

const ID_COLUMN = createColumn<List>({ displayName: 'ID', dataProperty: 'id' });

const NAME_COLUMN = createColumn<List>({
  displayName: 'Name',
  dataProperty: 'name',
  width: 300,
  type: CUSTOM,
  getCellContent: ({ rowData: list }) => <NameCell list={list} />,
});

const TYPE_COLUMN = createColumn<List, 'emailListType'>({
  displayName: 'Type',
  dataProperty: 'emailListType',
  formatter: (value, list) => {
    const BadgeComponent = STATE_BADGE[value];
    const Icon = STATE_ICONS[value];
    return (
      <div data-test={`${list.name}-listType`}>
        <BadgeComponent icon={<Icon />}>{value}</BadgeComponent>
      </div>
    );
  },
});

const CREATED_AT_COLUMN = createColumn<List, 'createdAtRaw'>({
  displayName: 'Created',
  dataProperty: 'createdAtRaw',
  getTooltip: ({ createdAtRaw }) => displayDate(createdAtRaw, DATE_FORMAT_FULL_WITH_TIME),
  formatter: (value) => moment(value).fromNow(),
});

const CREATED_BY_COLUMN = createColumn<List>({
  displayName: 'Created by',
  dataProperty: 'creatorUserId',
});

const UPDATED_AT_COLUMN = createColumn<List, 'updatedAtRaw'>({
  displayName: 'Updated',
  dataProperty: 'updatedAtRaw',
  getTooltip: ({ updatedAtRaw }) => displayDate(updatedAtRaw, DATE_FORMAT_FULL_WITH_TIME),
  formatter: (value) => moment(value).fromNow(),
});

const CONTACTS_COLUMN = createColumn<List, 'contacts'>({
  displayName: 'Contacts',
  dataProperty: 'contacts',
  type: CUSTOM,
  getCellContent: ({ rowData: list }) => <ContactCountCell list={list} />,
});

const DELETE_COLUMN = createColumn<List>({
  sortable: false,
  type: CUSTOM,
  getCellContent: ({ rowData: list }) => <DeleteListCell list={list} />,
});

export const COLUMNS = [
  ID_COLUMN,
  NAME_COLUMN,
  TYPE_COLUMN,
  CREATED_AT_COLUMN,
  CREATED_BY_COLUMN,
  UPDATED_AT_COLUMN,
  CONTACTS_COLUMN,
  DELETE_COLUMN,
];
